import clsx from 'clsx';
import { FC, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import { Loading } from '@components/index';
import {
  ToastifyCloseError,
  ToastifyCloseSuccess,
  ToastifyError,
  ToastifySuccess,
} from '@helpers/index';
import { useAuth } from '@modules/auth';
import { getCustomerSelector } from '@modules/customer/core/selectors';
import {
  initDashboardRequest,
  subscriptionChanged,
} from '@modules/dashboard/core/dashboardActions';
import ShopPromoModal from '@modules/shop/promo/ShopPromoModal';
import { PuppyToAdultBanner } from '@modules/subscriptions/components/pet-transition/PuppyToAdultBanner';
import {
  PetUpdateIssueBanner,
  PetUpdateIssueModal,
} from '@modules/subscriptions/components/pet-update-issue';
import {
  getLoadingMessageSelector,
  getPetSelector,
  getSubscriptionsSelector,
} from '@modules/subscriptions/core/selectors';
import { getPendingSelector as getSubscriptionsPendingSelector } from '@modules/subscriptions/core/selectors';
import { useSubscriptions } from '@modules/subscriptions/hooks/useSubscriptions';

import { MenuComponent } from '../assets/ts/components';
import { WithChildren } from '../helpers';
import {
  themeModeSwitchHelper,
  useThemeMode,
} from '../partials/layout/theme-mode/ThemeModeProvider';
import { Aside } from './components/aside/Aside';
import { Content } from './components/Content';
import { Footer } from './components/footer/Footer';
import { HeaderWrapper } from './components/header/HeaderWrapper';
import { MobileMenu } from './components/header/mobile-menu/MobileMenu';
import { PageDataProvider } from './core';

export const MasterLayout: FC<WithChildren> = () => {
  const dispatch = useDispatch();

  const { mode } = useThemeMode();
  const location = useLocation();

  const pet = useSelector(getPetSelector);
  const isPuppy = pet?.dog_stage === 'puppy';

  const customer = useSelector(getCustomerSelector);
  const subscriptions = useSelector(getSubscriptionsSelector);
  const { defaultSubscription, setDefaultSubscription } = useSubscriptions();
  const prevSubscriptionId = useRef<number | undefined>(undefined);

  const { refreshToken } = useAuth();

  // DOM: reinitialize menu components on route change
  useEffect(() => {
    setTimeout(() => {
      MenuComponent.reinitialization();
    }, 500);
  }, [location.key]);

  // NAV: scroll to top on every route change (mobile + desktop).
  // Centralizes the behavior so each page no longer needs its own scrollTo.
  // Deferred a tick so lazily-loaded route content is mounted before scrolling.
  useEffect(() => {
    const id = setTimeout(() => {
      window.scrollTo({ top: 0, left: 0 });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 0);
    return () => clearTimeout(id);
  }, [location.pathname]);

  // DOM: sync theme mode
  useEffect(() => {
    themeModeSwitchHelper(mode);
  }, [mode]);

  // INIT: dispatch a single action — the saga handles all fetching in parallel
  useEffect(() => {
    refreshToken();
    dispatch(initDashboardRequest());
  }, [dispatch, refreshToken]);

  // Set default subscription once subscriptions arrive (if not already set by saga)
  useEffect(() => {
    if (!defaultSubscription && subscriptions.length) {
      setDefaultSubscription(subscriptions);
    }
  }, [defaultSubscription, subscriptions, setDefaultSubscription]);

  // When the default subscription changes, notify the saga to re-fetch dependent data
  useEffect(() => {
    if (
      defaultSubscription?.id &&
      prevSubscriptionId.current !== undefined &&
      prevSubscriptionId.current !== defaultSubscription.id
    ) {
      dispatch(subscriptionChanged({ subscriptionId: defaultSubscription.id }));
    }
    prevSubscriptionId.current = defaultSubscription?.id;
  }, [defaultSubscription?.id, dispatch]);

  const loadingMessage = useSelector(getLoadingMessageSelector);
  const isPendingSubscriptions = useSelector(getSubscriptionsPendingSelector);

  // Show paw spinner during initial subscription load, customer load, or explicit actions
  const showSpinner = isPendingSubscriptions || !customer || !!loadingMessage;

  // DOM: toggle body class for pages that use the paw-pattern background
  useEffect(() => {
    const PAWS_PATHS = [
      '/store-credit',
      '/subscriptions',
      '/orders',
      '/dashboard',
      '/mitbestellen',
      '/profile',
    ];
    if (PAWS_PATHS.includes(location.pathname)) {
      document.body.classList.add('paws-bg');
    } else {
      document.body.classList.remove('paws-bg');
    }
  }, [location.pathname]);

  return (
    <PageDataProvider>
      <ToastContainer
        position="top-center"
        icon={({ type }) => {
          if (type === 'success') return <ToastifySuccess />;
          if (type === 'error') return <ToastifyError />;
        }}
        closeButton={({ type }) => {
          if (type === 'error') return <ToastifyCloseError />;
          return <ToastifyCloseSuccess />;
        }}
      />
      {showSpinner && <Loading message={loadingMessage || undefined} />}
      {/*<HolidayNotificationTwo /> */}
      <ShopPromoModal />
      {/* Pet "update required" flow — mounted at the layout level so the
          intro modal (and its banner fallback) appear on every page, not
          just the dashboard. */}
      <PetUpdateIssueModal />
      <div className="flex flex-row page px-[15px] md:px-[32px] mt-[15px] md:mt-[40px] md:w-full lgup:px-[40px] max-w-[1600px] mx-auto">
        <div
          className="flex flex-col flex-auto wrapper mb-[8.5rem] w-full  mx-auto lg:mb-0"
          id="kt_wrapper">
          {isPuppy && <PuppyToAdultBanner />}
          <PetUpdateIssueBanner />
          <HeaderWrapper />

          <div id="kt_content" className="flex flex-col content">
            {/* <Toolbar /> */}
            <div className="justify-between mx-auto md:flex w-full h-screen" id="kt_post">
              <Content>
                <div
                  className={clsx('w-full flex main-content justify-center', {
                    '!p-0 mt-[40px] lg:mt-0': location.pathname === '/store-credit',
                  })}>
                  <Aside />
                  <div
                    className={clsx('right-side w-full', {
                      'right-side--store-credit': location.pathname === '/store-credit',
                      'right-side--subscriptions': location.pathname === '/subscriptions',
                    })}>
                    {customer ? <Outlet /> : null}
                  </div>
                </div>
                <div className="lg:hidden">
                  <Footer />
                </div>
                <div className="hidden lg:block ml-[255px]">
                  <Footer />
                </div>
              </Content>
            </div>
          </div>

          <MobileMenu />
        </div>
      </div>
    </PageDataProvider>
  );
};
